/* eslint-disable i18next/no-literal-string -- DOM tag names, outline punctuation and agent-facing placeholders, none of it user-visible copy */
const MAX_OUTLINE_LENGTH = 10000;
const TRUNCATION_SUFFIX = '...';
const INDENT = '  ';
const LIST_ITEM_MARKER = '- ';
const LIST_ITEM_TAG = 'li';
const VIDEO_PLACEHOLDER = '[video]';
const IMAGE_PLACEHOLDER = '[image]';

/** Tags whose text is markup or styling rather than content the user reads. */
const SKIPPED_TAGS = new Set(['script', 'style', 'noscript', 'svg']);

/** Tags that continue the current line, so a sentence with a bold word stays on one line. */
const INLINE_TAGS = new Set([
  'a',
  'abbr',
  'b',
  'bdi',
  'bdo',
  'cite',
  'code',
  'del',
  'dfn',
  'em',
  'i',
  'ins',
  'kbd',
  'label',
  'mark',
  'output',
  'q',
  's',
  'samp',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'u',
  'var',
]);
/* eslint-enable i18next/no-literal-string */

function tagOf(element: Element): string {
  return element.tagName.toLowerCase();
}

/** Visible means any part of the element intersects the container's bounds. */
function isElementVisible(element: Element, containerRect: DOMRect): boolean {
  const rect = element.getBoundingClientRect();

  return (
    rect.bottom > containerRect.top &&
    rect.top < containerRect.bottom &&
    rect.right > containerRect.left &&
    rect.left < containerRect.right &&
    rect.width > 0 &&
    rect.height > 0
  );
}

function collectVisibleElements(container: HTMLElement): Set<Element> {
  const containerRect = container.getBoundingClientRect();
  const visible = new Set<Element>();

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  container.querySelectorAll('*').forEach((element) => {
    if (!isElementVisible(element, containerRect)) return;

    visible.add(element);
    // A wrapper can measure as empty (`display: contents`, a zero-height flex
    // parent) while the children inside it render, so visibility propagates up.
    let parent = element.parentElement;
    while (parent && parent !== container) {
      visible.add(parent);
      parent = parent.parentElement;
    }
  });

  return visible;
}

/** Media and controls keep their meaning as text; their real attributes do not. */
function placeholderFor(element: Element): string | null {
  switch (tagOf(element)) {
    case 'video':
      return VIDEO_PLACEHOLDER;
    case 'img': {
      // eslint-disable-next-line i18next/no-literal-string -- DOM attribute name
      const alt = element.getAttribute('alt');
      return alt ? `[image: ${alt}]` : IMAGE_PLACEHOLDER;
    }
    case 'button': {
      const label = element.getAttribute('aria-label') || element.textContent?.trim();
      return label ? `[button: ${label}]` : null;
    }
    default:
      return null;
  }
}

function shouldSkip(element: Element, visible: Set<Element>): boolean {
  return SKIPPED_TAGS.has(tagOf(element)) || !visible.has(element);
}

/** Flattens an inline subtree, so an image wrapped in a link is not dropped. */
function collectInlineText(element: Element, visible: Set<Element>): string {
  let text = '';

  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const child = node as Element;
    if (shouldSkip(child, visible)) return;

    const placeholder = placeholderFor(child);
    text += placeholder ? ` ${placeholder} ` : collectInlineText(child, visible);
  });

  return text;
}

/** A widget rendering markup as text would otherwise put a tag back in the body. */
function escapeAngleBrackets(text: string): string {
  return text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function appendOutline(
  element: Element,
  depth: number,
  visible: Set<Element>,
  lines: string[],
): void {
  const marker = tagOf(element) === LIST_ITEM_TAG ? LIST_ITEM_MARKER : '';
  let pending = '';
  let emitted = false;

  const flush = (): void => {
    const text = escapeAngleBrackets(pending.replace(/\s+/g, ' ').trim());
    pending = '';
    if (!text) return;

    lines.push(INDENT.repeat(depth) + (emitted ? '' : marker) + text);
    emitted = true;
  };

  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pending += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const child = node as Element;
    if (shouldSkip(child, visible)) return;

    const placeholder = placeholderFor(child);
    if (placeholder) {
      pending += ` ${placeholder} `;
      return;
    }
    if (INLINE_TAGS.has(tagOf(child))) {
      pending += collectInlineText(child, visible);
      return;
    }

    flush();
    // Depth only grows where a line was emitted, so chains of layout wrappers
    // collapse instead of indenting into a staircase.
    appendOutline(child, emitted ? depth + 1 : depth, visible, lines);
  });

  flush();
}

/**
 * Describes what is currently VISIBLE inside a container as indented plain text,
 * used as a fallback when a widget does not describe its own UI context.
 *
 * The output is deliberately tag-free: this text rides along with every chat
 * message sent while a widget panel is open, and the Cloud Armor policy in front
 * of the insights ingresses rejects request bodies containing HTML tags.
 */
export function extractVisibleOutline(container: HTMLElement): string {
  const lines: string[] = [];
  appendOutline(container, 0, collectVisibleElements(container), lines);

  const outline = lines.join('\n');

  return outline.length > MAX_OUTLINE_LENGTH
    ? outline.substring(0, MAX_OUTLINE_LENGTH) + TRUNCATION_SUFFIX
    : outline;
}
